import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';

@Component({
  selector: 'fi-day-range-picker',
  templateUrl: './day-range-picker.component.html',
  styleUrls: ['./day-range-picker.component.scss'],
  standalone: false,
})
export class DayRangePickerComponent implements OnInit, OnChanges {
  @Input() fromDate?: Date | null;
  @Input() toDate?: Date | null;
  @Input() unfocused = false;

  @Output() dateRangeSelected = new EventEmitter<Date[]>();

  shownYear: number;
  shownMonth: number;
  days: ((Date | undefined)[])[] = [];
  now = new Date();

  ngOnInit(): void {
    this.syncShownDate();
    this.refresh();
  }

  ngOnChanges(): void {
    this.syncShownDate();
    this.refresh();
  }

  incrementMonth(): void {
    const day = new Date(this.shownYear, this.shownMonth, 1, 0, 0, 0, 0);
    day.setMonth(day.getMonth() + 1);
    this.shownYear = day.getFullYear();
    this.shownMonth = day.getMonth();
    this.refresh();
  }

  decrementMonth(): void {
    const day = new Date(this.shownYear, this.shownMonth, 1, 0, 0, 0, 0);
    day.setMonth(day.getMonth() - 1);
    this.shownYear = day.getFullYear();
    this.shownMonth = day.getMonth();
    this.refresh();
  }

  refresh(): void {
    const day = new Date(this.shownYear, this.shownMonth, 1, 0, 0, 0, 0);
    const firstDayOfNextMonth = new Date(day);
    firstDayOfNextMonth.setMonth(firstDayOfNextMonth.getMonth() + 1);
    this.now = new Date();
    this.days = [[]];
    let lastWeek = this.days[0];
    const offset = (day.getDay() + 6) % 7;

    for (let i = 0; i < offset; ++i) {
      lastWeek.push(undefined);
    }

    while (day < firstDayOfNextMonth) {
      if (lastWeek.length > 6) {
        lastWeek = [];
        this.days.push(lastWeek);
      }

      lastWeek.push(new Date(day));
      day.setDate(day.getDate() + 1);
    }

    while (this.days.length < 6) {
      this.days.push([undefined]);
    }

    this.days.forEach(week => week.length = 7);
  }

  selectDay(day: Date): void {
    const selectedDay = new Date(day);

    if (!this.fromDate || this.toDate) {
      this.fromDate = selectedDay;
      this.toDate = undefined;
      this.dateRangeSelected.emit([this.fromDate, this.toDate]);
      return;
    }

    if (selectedDay < this.fromDate) {
      this.toDate = this.fromDate;
      this.fromDate = selectedDay;
    } else {
      this.toDate = selectedDay;
    }

    this.dateRangeSelected.emit([this.fromDate, this.toDate]);
  }

  isToday(day: Date): boolean {
    return day.getFullYear() === this.now.getFullYear()
      && day.getMonth() === this.now.getMonth()
      && day.getDate() === this.now.getDate();
  }

  isRangeStart(day: Date): boolean {
    return this.isSameDate(day, this.fromDate);
  }

  isRangeEnd(day: Date): boolean {
    return this.isSameDate(day, this.toDate);
  }

  isInRange(day: Date): boolean {
    if (!this.fromDate || !this.toDate) {
      return false;
    }

    return day > this.fromDate && day < this.toDate;
  }

  private syncShownDate(): void {
    const baseDate = this.fromDate ?? this.toDate ?? new Date();
    this.shownYear = baseDate.getFullYear();
    this.shownMonth = baseDate.getMonth();
  }

  private isSameDate(day: Date, selectedDay?: Date | null): boolean {
    return !!selectedDay
      && day.getFullYear() === selectedDay.getFullYear()
      && day.getMonth() === selectedDay.getMonth()
      && day.getDate() === selectedDay.getDate();
  }
}
